import React, { Suspense, useState, useEffect } from 'react';
import { Spin, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import type { SupportedLanguage } from '../i18n';
import { createContextLogger } from '../utils/logger';

const logger = createContextLogger('MDXLoader');

interface MDXLoaderProps {
  path: string;
  language: SupportedLanguage;
}

// Create dynamic import functions for each language
const createMdxImporter = (language: SupportedLanguage, path: string) => {
  return () => import(`./content/${language}/${path}.mdx`);
};

// Fallback to English if the requested language file doesn't exist
const createFallbackImporter = (path: string) => {
  return () => import(`./content/en/${path}.mdx`);
};

const MDXLoader: React.FC<MDXLoaderProps> = ({ path, language }) => {
  const { t } = useTranslation();
  const [MDXContent, setMDXContent] = useState<React.ComponentType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUsingFallback, setIsUsingFallback] = useState(false);

  useEffect(() => {
    const loadMDX = async () => {
      try {
        setLoading(true);
        setError(null);
        setIsUsingFallback(false);
        
        logger.debug(`Loading MDX file: ${language}/${path}`);
        
        let module;
        
        try {
          // Try to load the file in the requested language
          const importFn = createMdxImporter(language, path);
          module = await importFn();
        } catch (languageError) {
          logger.debug(`File not found for language ${language}, falling back to English`);
          
          // If the file doesn't exist in the requested language, fallback to English
          try {
            const fallbackImportFn = createFallbackImporter(path);
            module = await fallbackImportFn();
            setIsUsingFallback(language !== 'en');
          } catch (fallbackError) {
            throw new Error(`No MDX file found for path: ${path} in any language`);
          }
        }
        
        logger.debug('MDX module loaded:', module);
        
        if (module.default) {
          setMDXContent(() => module.default);
        } else {
          throw new Error('MDX module does not have a default export');
        }
      } catch (err) {
        logger.error(`Failed to load MDX file: ${language}/${path}.mdx`, err);
        setError(t('documentation.errors.loadErrorDescription', { page: path }));
      } finally {
        setLoading(false);
      }
    };

    loadMDX();
  }, [path, language, t]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <Spin size="large" tip={t('documentation.loading')} />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message={t('documentation.errors.loadError')}
        description={error}
        type="error"
        showIcon
        style={{ margin: '20px 0' }}
      />
    );
  }

  if (!MDXContent) {
    return (
      <Alert
        message={t('documentation.errors.noContent')}
        description={t('documentation.errors.noContentDescription')}
        type="info"
        showIcon
        style={{ margin: '20px 0' }}
      />
    );
  }

  return (
    <>
      {isUsingFallback && (
        <Alert
          message="Language Fallback"
          description={`This page is not available in ${t(`languages.${language}`)}. Showing English version instead.`}
          type="warning"
          showIcon
          style={{ marginBottom: '16px' }}
          closable
        />
      )}
      <MDXContent />
    </>
  );
};

export default MDXLoader; 